import type { AnomalyKind, Component, Flag, Marketplace, MonitorRow, PfcError } from "./api";

// C5 v2.10 (DEC-1424 PB-84): each channel is shown from the month its fees are complete. Amazon: the settlement loader holds
// every file posted from 2026-02-28 (live 2026-03-10). Shopify and Wholesale: the 3PL billing files start 2026-03-31.
// Earlier Amazon history is DEBT-1 (PFC MASTER D13). HISTORY_FLOOR is the earliest start.
export const CHANNEL_HISTORY_START = { Amazon: "2026-03", Shopify: "2026-04", Wholesale: "2026-04" } as const;
export type HistoryChannel = keyof typeof CHANNEL_HISTORY_START;
export const HISTORY_FLOOR = "2026-03";

// C5 v2.11: the P&L tie view starts at the Cost History floor and defaults to last month.
export const TIE_FLOOR = HISTORY_FLOOR;
export function tieMonth(value: unknown, thisMonth: string): string {
  const [y, m] = thisMonth.split("-").map(Number);
  const last = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
  const wanted = typeof value === "string" && /^\d{4}-\d{2}$/.test(value) ? value : last;
  return wanted < TIE_FLOOR ? TIE_FLOOR : wanted > last ? last : wanted;
}

export function historyChannel(channelLocation: string): HistoryChannel {
  if (channelLocation.startsWith("amazon_")) return "Amazon";
  if (channelLocation.startsWith("wholesale_")) return "Wholesale";
  return "Shopify";
}

export function historyStartFor(channelLocation: string): string {
  return CHANNEL_HISTORY_START[historyChannel(channelLocation)];
}

export function beforeHistoryStart(channelLocation: string, month: string): boolean {
  return month.slice(0, 7) < historyStartFor(channelLocation);
}

// The first shown month of a key: its change is against a hidden month, so it is not shown.
export function atHistoryStart(channelLocation: string, month: string): boolean {
  return month.slice(0, 7) === historyStartFor(channelLocation);
}

export function sixMonthWindow(thisMonth: string): { from: string; to: string } {
  const [year, month] = thisMonth.split("-").map(Number);
  const start = new Date(Date.UTC(year, month - 6, 1));
  const from = `${start.getUTCFullYear()}-${String(start.getUTCMonth() + 1).padStart(2, "0")}`;
  return { from: clampHistoryMonth(from), to: thisMonth };
}

export function shiftMonth(month: string, delta: number): string {
  if (!/^\d{4}-\d{2}$/.test(month)) return month;
  const [year, monthNumber] = month.split("-").map(Number);
  const shifted = year * 12 + monthNumber - 1 + delta;
  const shiftedYear = Math.floor(shifted / 12);
  const shiftedMonth = shifted - shiftedYear * 12 + 1;
  return `${String(shiftedYear).padStart(4, "0")}-${String(shiftedMonth).padStart(2, "0")}`;
}

// C5 v2.9.4: 3PL History opens on the last 8 months with data (Bojan, DEC-1596 PB-124, 3 Nov 2026; was 12 in C5 v2.9.3).
export const TPL_DEFAULT_MONTHS = 8;

export const COMPONENT_LABEL: Record<Component, string> = {
  referral: "Referral Fee",
  dsf: "Digital Fee",
  fba_per_unit: "Fulfilment Fee",
  courier: "Fulfilment Fee",
  pick_pack: "Pick & Pack",
  packaging: "Packaging",
  other: "Other Fees",
};

export const MONITOR_SORTS = ["sku", "product", "variant", "region", "channel", "location", "fee", "referral", "dsf", "fulfilment", "pick_pack", "packaging", "other", "flag", "age"] as const;
export type MonitorSort = typeof MONITOR_SORTS[number];

export function sortDirection(currentId: string | undefined, currentDirection: "asc" | "desc", nextId: string): "asc" | "desc" {
  return currentId === nextId && currentDirection === "asc" ? "desc" : "asc";
}

export const COPY = {
  tieHeader: "Each company's Cost History for the month against its channel accounts in the live Xero P&L, with every difference named. From March 2026.",
  freshness: (date: string, run: string) => `Data to ${date} · source: Precision Fulfilment Costs · run ${run} · next run Monday 06:00`,
  partial: (component: string, store: string) => `This run was partial: ${component} carried from the previous run (${store}).`,
  late: (date: string) => `The Monday run did not finish by 07:00. Values shown are from ${date}.`,
  emptyMonitor: "No run has completed yet. Press Refresh to run the first one.",
  enterProjection: "Enter projection",
  needsHelper: (sku: string, market: string) => `No fee yet for ${sku} on ${market}. Start from a similar SKU or type the values.`,
  projectionSaved: "Saved as PROJECTED. Applies on the next run, or press Refresh.",
  correctionSaved: (id: number | string) => `Saved. CORRECTED until a billed value lands. Audit ${id}.`,
  futureSaved: (date: string) => `Saved as a pending rate version. The current rate stays live; this one applies from ${date}.`,
  futureReplaced: (n: number) => ` Replaced ${n} earlier pending ${n === 1 ? "version" : "versions"} for this fee.`,
  withdrawRatePrompt: "Why are you withdrawing this pending rate version?",
  rateWithdrawn: "Pending rate version withdrawn. The current rate stays live.",
  pendingScope: "whole marketplace",
  futureResolutionMissing: "Future changes are not available from this engine version. The Rates tab lists every pending rate.",
  futureFollows: "moves with the Referral and Fulfilment fees",
  pastDate: "That date has already arrived. Use Correct a fee instead.",
  duplicateFuture: "A change for that date already exists. Withdraw it first.",
  ruleSaved: (n: number | string, id: number | string) => `Saved. Version ${n}. Applies on the next run. Audit ${id}.`,
  ceilingsTitle: "FBA fee ceilings",
  ceilingsHelp: "The highest per-unit FBA fee the engine accepts as plausible in each settlement currency; a billed fee above it raises an FBA ceiling anomaly. Assumed means seeded at build and not yet confirmed. Confirm keeps the value and records today as the confirmed date; a new value saves a new version.",
  ceilingSaved: (currency: string, n: number, id: number | string) => `Saved. ${currency} ceiling version ${n}. Audit ${id}.`,
  assumed: "assumed",
  inactive: "inactive",
  activeHelp: "Inactive: the engine feeds no fees and raises no anomalies for this marketplace; billed history stays.",
  vCeiling: "Enter a ceiling above zero.",
  emptyFiles: "No billing file read yet. Drop the J&J export or the Omnipack workbook in its Drive folder and press Refresh.",
  otherCostsEmpty: "No month loaded yet. Load a company and month from Xero, or enter the account totals, to start.",
  loadHelp: "Reads the month's account totals from the Xero P&L. A total already typed for an account Xero carries is replaced; one Xero does not carry stays as typed.",
  monthClosedForLoad: "This month is closed, so nothing was loaded. Reopen it, load, then close it again.",
  xeroUnreadable: (detail: string) => `The Xero figures could not be read, so nothing was loaded${detail ? ` (${detail})` : ""}. Try again later, or enter the totals by hand.`,
  closeBlocked: (flags: number, unmapped: number) => `Cannot close: ${flags} tie-out flag(s) open, ${unmapped} account(s) without a scope.`,
  closeConfirmed: (version: number, fees: number) => `Month closed at v${version}. ${fees} trailing fee(s) refreshed; Precision Pricing receives them on the next run.`,
  accountsSaved: (n: number) => `${n} account total(s) saved. Open the month to review the tie-out.`,
  addFolder: (threepl: string) => `Add the Drive folder for ${threepl}`,
  sourceSaved: "Folder saved. It is checked on every run; press Refresh to read it now.",
  overlap: (n: number, earlier: string, m: number) => `${n} lines already read from ${earlier}; ${m} new lines written.`,
  // The engine's ledger `reason` already ends in "(row N)" when a row is known (parsers/common.py ParserError).
  rejected: (reason: string) => `Not read: ${reason}. Nothing was written.`,
  folderUnreachable: "We can't reach the billing folder. Files already read are unaffected.",
  incompleteOrder: "Pick and pack billed, courier not yet: waits for the next Omnipack file.",
  permission: "You do not have permission to change this. Ask the Margin Engine area owner.",
  engineDown: "We can't reach the fulfilment costs engine, so nothing is shown. Try again shortly.",
  notConfigured: "The engine address is not set on the portal yet. Ask the portal owner.",
  runInFlight: "A refresh is already running, following it.",
  stillWaiting: "Still waiting for the run…",
  runOk: "Run ok",
  runPartial: (component: string) => `Run partial: ${component}`,
  runFailed: (note: string) => `Run failed: ${note}`,
  vFee: "Enter a fee above zero.",
  vReason: "Say why you are changing this.",
  vSource: "Name the document or notice this comes from.",
  checkField: "Check the highlighted field.",
  historyEmpty: "No billed months yet for this selection. Amazon months appear after the first settlement; Shopify months after the first billing file.",
  historyAllFees: (currency: string | null) => `Every fee PFC carries for this SKU and channel, per unit and per month, ${currency ? `${currency} ` : ""}net of VAT. Each row divides the amount of that month by the units billed in it (shown below), so a low-unit month can jump and a value can differ from the Fulfilment Index per unit.`,
  historyPerCase: "Wholesale case SKU: every value is per case, and the Cases row counts the cases billed.",
  historyOff: "Cost history is not switched on in the engine yet.",
  historyRange: "Choose up to 36 months.",
  historyStarts: "Fees are complete from 2026-03 for Amazon and from 2026-04 for Shopify and Wholesale. Amazon fees come from the settlement files PFC holds from 28 February 2026 on; earlier Amazon fees are not loaded yet. Shopify and wholesale fees come from the 3PL billing files, which start on 31 March 2026.",
  historyBeforeStart: (channel: string, start: string) => `${channel} fees are complete from ${start}; earlier months are not shown.`,
  historyDrawerStart: (channel: string, start: string) => `Shown from ${start}: ${channel} fees are complete from then.`,
  historyPeriodEmpty: (from: string, to: string) => `Nothing to show: To (${to}) is before From (${from}). Cost History starts ${HISTORY_FLOOR}: Amazon fees before then are only partly loaded (from 28 February 2026), and the 3PL billing files start on 31 March 2026.`,
  historyFloorNotice: `Cost History starts ${HISTORY_FLOOR}: Amazon fees before then are only partly loaded (from 28 February 2026), and the 3PL billing files start on 31 March 2026, so earlier months are not shown.`,
  tplHistoryOff: "3PL History is not available on this engine yet.",
  tplEmpty: "No months loaded for this provider. Load the inputs or check the Drive files.",
  tplEmptySeveral: "No months loaded for these 3PLs. Load the inputs or check the Drive files.",
  tplMixedCurrency: "These 3PLs bill in different currencies, so amounts are in GBP.",
  tplRange: "Choose up to 72 months.",
  tplNoMatch: "No 3PL matches these filters.",
  tplNoProviders: "No 3PLs are set up yet.",
  historyHeader: "Billed fulfilment cost per unit, net of VAT, by the month the order or settlement was posted. Months do not change once billed.",
  anomaliesTitle: "Amazon fees beyond their band or ceiling",
  anomaliesPage: (page: number, pages: number) => `Page ${page} of ${pages}`,
  anomaliesEmpty: (run: string) => `No Amazon fee beyond its band or ceiling since run ${run}.`,
  anomaliesOff: "Anomaly checks are not switched on in the engine yet.",
  anomaliesHelp: "Four checks on billed Amazon fees. Amazon rate vs registry: a Referral, DSF or Fulfilment fee beyond its allowed band (1%) against its registry rule; Accept as new rate updates the registry to the billed value (Referral and Fulfilment), Mark reviewed records a real DSF change. FBA fee above the ceiling: a billed Fulfilment fee above the currency ceiling; check the fee, raise the ceiling under FBA fee ceilings if it is real, then Mark reviewed. FBA fee moved vs last feed: no rule exists for this SKU and the fee moved beyond the band; Mark reviewed if the billed value is right. DSF billed with no rule: set the marketplace's DSF percentages under Marketplace fee rules, then Mark reviewed. One-off, keep rate: the line is right for a one-off cause; give the reason.",
  confirmRate: "Accept as new rate",
  confirm: "Mark reviewed",
  dismiss: "One-off, keep rate",
  dismissReason: "Say why this is a one-off.",
  confirmedRate: (key: string, from: string, to: string, n: number, id: string) => `Rate updated for ${key}: ${from} to ${to}. Version ${n}. Audit ${id}.`,
  confirmedLane: "Marked reviewed. Nothing else to change.",
  dismissed: "Kept the rate. Stays in the history.",
  shopifyAnomaliesTitle: "Shopify 3PL costs beyond their band",
  shopifyAnomaliesEmpty: (run: string) => `No Shopify 3PL cost beyond its band since run ${run}.`,
  shopifyAnomaliesHelp: "Two checks on billed 3PL costs per order, net of VAT, for each store and ship-to country. Parcel vs lane mean: the pick and pack, packaging or courier cost of one order is above the per-order band over the mean per order of its store and country in the 90 days before the order month; check the 3PL bill for a surcharge or an extra parcel; a large basket raises pick and pack. Lane mean moved: the mean per order of a store and country in the month the latest 3PL file billed moved beyond the lane band against the 90 days before; the 3PL may have changed its price or service. Mark reviewed: the cost is real; a lane stays closed while its monthly mean stays within the band of the reviewed value. One-off: the cost is wrong or unusual for a one-off cause; give the reason. A parcel to a country with too few orders is measured against its store mean over every country, at twice the band. Lanes with too few orders and pick and pack lanes (which move with basket size) have no lane check, and EU packaging (a monthly materials share) has no check.",
  shopifyDismiss: "One-off",
  shopifyDismissed: "Closed as a one-off. Stays in the history.",
  changesTitle: (run: string, n: number) => `Changes since run ${run} (${n})`,
  changesNone: (run: string) => `No changes since run ${run}.`,
  changesCapped: (n: number) => `and ${n} more, see Cost History`,
  beyondBand: "beyond band",
  viewFee: "Fee per month",
  viewChange: "Change vs previous month",
  viewPct: "Change vs previous month (%)",
  legendFee: "fee per unit as fed; a dash means no billed value that month; red when up more than 0.01 in the row's currency against the previous month, green when down more than 0.01",
  legendChange: "amount up or down against the previous month; blank for the first month and after a gap; red when up more than 0.01 in the row's currency against the previous month, green when down more than 0.01",
  legendPct: "percent up or down against the previous month; blank for the first month and after a gap; red when up more than 0.01 in the row's currency against the previous month, green when down more than 0.01",
  sortHint: "Click a column heading to sort; click again to flip.",
} as const;

export const KIND_NAME: Record<AnomalyKind, string> = {
  amazon_rate: "Amazon rate vs registry",
  amazon_drift: "FBA fee moved vs last feed",
  amazon_ceiling: "FBA fee above the ceiling",
  dsf_unmodelled: "DSF billed with no rule",
  shopify_order: "order vs its lane standard",
  shopify_lane: "lane shift vs last standard",
  shopify_parcel: "parcel vs lane mean",
  shopify_lane_move: "lane mean moved",
};
export const AMAZON_KINDS: AnomalyKind[] = ["amazon_rate", "amazon_drift", "amazon_ceiling", "dsf_unmodelled"];
// C5 v2.5: the Shopify card lists the C2 v1.17 checks only; the retired shopify_order and shopify_lane rows are closed.
export const SHOPIFY_KINDS: AnomalyKind[] = ["shopify_parcel", "shopify_lane_move"];
export type AnomalyChannel = "amazon" | "shopify";
export const CARD_KINDS: Record<AnomalyChannel, AnomalyKind[]> = { amazon: AMAZON_KINDS, shopify: SHOPIFY_KINDS };
export const CARD_COPY: Record<AnomalyChannel, { title: string; empty: (run: string) => string; help: string; keyHeader: string; dismiss: string; dismissed: string }> = {
  amazon: { title: COPY.anomaliesTitle, empty: COPY.anomaliesEmpty, help: COPY.anomaliesHelp, keyHeader: "SKU or lane", dismiss: COPY.dismiss, dismissed: COPY.dismissed },
  shopify: { title: COPY.shopifyAnomaliesTitle, empty: COPY.shopifyAnomaliesEmpty, help: COPY.shopifyAnomaliesHelp, keyHeader: "Order or lane", dismiss: COPY.shopifyDismiss, dismissed: COPY.shopifyDismissed },
};
export const CAUSE_NAME: Record<string, string> = { settlement: "settlement", "billing file": "billing file", correction: "correction", projection: "projection", "registry edit": "registry edit", carried: "carried", "first run": "first run" };

export function messageFor(err: unknown): string {
  const e = err as PfcError;
  if (!e || typeof e !== "object" || !("status" in e)) return COPY.engineDown;
  if (e.status === 403) return COPY.permission;
  if (e.status === 409 && e.body?.error === "month_closed") return COPY.monthClosedForLoad;
  if (e.status === 502 && e.body?.error === "xero_table_unreadable") return COPY.xeroUnreadable(e.body.message ?? "");
  if (e.status === 409 && e.body?.message?.startsWith("pfc:")) return e.body.message;
  if (e.status === 503 && e.body?.error === "not_configured") return COPY.notConfigured;
  if (e.status === 503) return COPY.engineDown;
  if (e.status === 422 && e.body?.message) return e.body.message;
  if (e.status === 422 && Array.isArray(e.body?.detail)) return e.body.detail[0]?.msg ?? COPY.checkField;
  if (e.status === 404 && e.body?.error === "run_unknown") return COPY.emptyMonitor;
  return e.body?.message ?? COPY.engineDown;
}
export function flagTone(flag: Flag): "danger" | "warn" | "purple" | "ok" | "neutral" {
  if (flag === "NEEDS PROJECTION") return "danger";
  if (flag === "CORRECTED" || flag === "STALE") return "warn";
  if (flag === "PROJECTED" || flag === "ESTIMATE") return "purple";
  if (flag === "BILLED") return "ok";
  return "neutral";
}
export function money(value: string | null | undefined, currency: string | null | undefined, dp = 2): string {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const sym = currency === "GBP" ? "£" : currency === "EUR" ? "€" : currency === "AED" ? "AED " : currency === "PLN" ? "zł" : currency === "SEK" ? "kr" : `${currency ?? ""} `;
  return `${sym}${n.toFixed(dp)}`;
}
export function age(days: number | null | undefined): string { return days === null || days === undefined ? "—" : `${days}d`; }
export function percentToFraction(input: string): string | null {
  const n = Number(input.replace("%", "").trim());
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return (n / 100).toFixed(4);
}
export function percentEcho(fraction: string): string { return `stored as ${(Number(fraction) * 100).toFixed(2).replace(/\.?0+$/, "")}%`; }
// C5 v1.2 rule: at most 2 decimals, trailing zeros trimmed ("1.65%", "2%"), dash when missing. Serves the Rates tab.
export function pct(fraction: string | null | undefined): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(Number(fraction))) return "—";
  return `${(Number(fraction) * 100).toFixed(2).replace(/\.?0+$/, "")}%`;
}

// A registry value renders as a percent for a rate (pct, pct_of_fees) and as money for an amount.
export function ruleValue(rule: { value: string; value_kind: string; currency: string | null }): string {
  return rule.value_kind === "amount" ? money(rule.value, rule.currency) : pct(rule.value);
}
// R4 anomaly values: fixed 2 decimals so billed and reference rates line up ("15.30%" vs "14.00%").
export function ratePct(fraction: string | null | undefined): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(Number(fraction))) return "";
  return `${(Number(fraction) * 100).toFixed(2)}%`;
}
export function dsfRule(fba: string | null, referral: string | null): string {
  const part = (value: string | null) => `${(Number(value ?? 0) * 100).toFixed(1)}%`;
  return `${part(fba)} of FBA fees + ${part(referral)} of SOA fees`;
}
export function clampHistoryMonth(month: string): string { return month < HISTORY_FLOOR ? HISTORY_FLOOR : month; }
export function historySortAllowed(value: string, months: string[]): boolean {
  return ["sku", "product", "variant", "region", "channel", "location"].includes(value) || months.includes(value);
}
export function monitorOptions(rows: MonitorRow[], key: "product" | "variant" | "country") {
  const values = new Map<string, string>();
  for (const row of rows) {
    const value = row[key];
    if (value) values.set(value, value);
  }
  return [...values].map(([value, label]) => ({ value, label }));
}
// Inactive marketplaces (engine marketplace_ref.active = false, switched on the Rates tab) stay out of the filters and selects;
// the Rates tab marketplace table still lists them with their badge so they can be switched back on.
export function visibleMarketplaces<T extends { active?: boolean }>(markets: T[]): T[] {
  return markets.filter((market) => market.active !== false);
}
// A null confirmed date means the value is assumed (engine rule: confirmed_on IS NULL), never unknown.
export function confirmedLabel(confirmedOn: string | null | undefined): string {
  return confirmedOn ?? COPY.assumed;
}
export function confirmWritesRate(anomaly: { kind: string; component: string }): boolean {
  return anomaly.kind === "amazon_rate" && anomaly.component !== "dsf";
}
export function locationOptions(markets: Array<Pick<Marketplace, "location">>): Array<{ value: string; label: string }> {
  const values = new Set([...markets.map((market) => market.location).filter(Boolean), "UK", "EU", "MENA"]);
  return [...values].sort((left, right) => left.localeCompare(right)).map((value) => ({ value, label: value }));
}
export const MOVE_THRESHOLD = 0.01;
export function moveClass(change: string | null | undefined): string | undefined {
  if (change === null || change === undefined) return undefined;
  const n = Number(change);
  if (!Number.isFinite(n)) return undefined;
  return n > MOVE_THRESHOLD ? "text-ks-danger font-bold" : n < -MOVE_THRESHOLD ? "text-ks-pass font-bold" : undefined;
}
export const MONITOR_PCT_THRESHOLD = 0.015;
export function pctMoveClass(changePct: string | null | undefined): string | undefined {
  if (changePct === null || changePct === undefined) return undefined;
  const n = Number(changePct);
  if (!Number.isFinite(n)) return undefined;
  if (n > MONITOR_PCT_THRESHOLD) return "text-ks-danger font-bold";
  if (n < -MONITOR_PCT_THRESHOLD) return "text-ks-pass font-bold";
  return "text-ks-grey-1";
}
export function countryOptions(values: Array<string | null | undefined>): Array<{ value: string; label: string }> {
  const set = new Set(values.filter((value): value is string => !!value && value !== "ALL"));
  return [...set].sort((left, right) => left.localeCompare(right)).map((value) => ({ value, label: value }));
}
export function signedPct(value: string | null | undefined, dp = 1): string {
  if (value === null || value === undefined) return "";
  const n = Number(value);
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toFixed(dp)}%`;
}
export function signedMoney(value: string | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined) return "";
  const n = Number(value);
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}${money(String(Math.abs(n)), currency)}`;
}
export const RATE_COMPONENTS: Component[] = ["referral", "dsf"];
export function anomalyValue(value: string, component: Component, currency: string | null): string {
  return RATE_COMPONENTS.includes(component) ? ratePct(value) : money(value, currency);
}
export function bandLabel(band: string): string { return `${(Number(band) * 100).toFixed(0)}%`; }
// Engine order for one list: open first, a lane move first (C5 v2.5), largest move first, newest first. Reproduced when four engine lists are concatenated.
export function sortAnomalies<T extends { status: string; change_pct: string; id: number; kind?: string }>(rows: T[]): T[] {
  return [...rows].sort((left, right) => {
    const open = Number(right.status === "open") - Number(left.status === "open");
    if (open !== 0) return open;
    const lane = Number(right.kind === "shopify_lane_move") - Number(left.kind === "shopify_lane_move");
    if (lane !== 0) return lane;
    const move = Math.abs(Number(right.change_pct)) - Math.abs(Number(left.change_pct));
    if (move !== 0) return move;
    return right.id - left.id;
  });
}
export function anomalyKey(anomaly: { kind: string; xero_sku: string; channel_location: string; marketplace_name: string | null; country: string; order_no: string | null }): string {
  if (anomaly.kind === "shopify_lane" || anomaly.kind === "shopify_lane_move") return `${anomaly.channel_location} · ${anomaly.country} lane`;
  if (anomaly.kind === "shopify_order" || anomaly.kind === "shopify_parcel") return `${anomaly.order_no} · ${anomaly.channel_location} · ${anomaly.country}`;
  if (anomaly.kind === "dsf_unmodelled") return `${anomaly.marketplace_name ?? anomaly.channel_location} · all SKUs`;
  return `${anomaly.xero_sku} · ${anomaly.marketplace_name ?? anomaly.channel_location}`;
}
// The card sub-line: the kind name and what the order count means for it (C5 v2.5).
export function anomalyCountNote(anomaly: { kind: AnomalyKind; order_count: number | null; reference_kind?: string }): string {
  const name = KIND_NAME[anomaly.kind];
  if (!anomaly.order_count) return name;
  if (anomaly.kind === "shopify_parcel" && anomaly.reference_kind === "store_baseline") return `${name} · store mean of ${anomaly.order_count} orders, few to this country`;
  if (anomaly.kind === "shopify_parcel") return `${name} · lane mean of ${anomaly.order_count} orders`;
  if (anomaly.kind === "shopify_lane_move") return `${name} · ${anomaly.order_count} orders in the billed month`;
  return `${name} · ${anomaly.order_count} orders`;
}
export function monthsBetween(from: string, to: string): string[] {
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  if (![fy, fm, ty, tm].every(Number.isFinite) || fy > ty || (fy === ty && fm > tm)) return [];
  const out: string[] = [];
  for (let y = fy, m = fm; y < ty || (y === ty && m <= tm); m === 12 ? (y++, m = 1) : m++) out.push(`${y}-${String(m).padStart(2, "0")}`);
  return out;
}
